import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Zap, Plus, Edit2, Trash2, Eye, 
    Loader, Search, Filter, CheckCircle, XCircle, Clock, FileText
} from 'lucide-react';
import { apiClient } from '../../config/api';

const AdminWorkflowTemplatesPage = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        loadTemplates();
    }, [categoryFilter, typeFilter]);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const result = await apiClient.get('/workflows/templates');
            setTemplates(result.templates || []);
        } catch (error) {
            console.error('Failed to load workflow templates:', error);
            alert('Failed to load workflow templates: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const deleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
            return;
        }

        try {
            await apiClient.delete(`/workflows/templates/${templateId}`);
            loadTemplates();
            alert('Template deleted successfully');
        } catch (error) {
            alert('Failed to delete template: ' + (error.response?.data?.error || error.message));
        }
    };

    const applyTemplate = async (templateId) => {
        try {
            const result = await apiClient.post(`/workflows/templates/${templateId}/apply`, {
                name: `Workflow from Template`,
                description: '',
            });
            const newWorkflowId = result.workflow.id;
            navigate(`/admin/workflow-builder/${newWorkflowId}`);
        } catch (error) {
            alert('Failed to apply template: ' + (error.response?.data?.error || error.message));
        }
    };

    const getCategoryBadge = (category) => {
        const badges = {
            sales: { color: 'bg-blue-100 text-blue-800', label: 'Sales' },
            service: { color: 'bg-green-100 text-green-800', label: 'Service' },
            welcome: { color: 'bg-purple-100 text-purple-800', label: 'Welcome' },
            reminder: { color: 'bg-yellow-100 text-yellow-800', label: 'Reminder' },
            general: { color: 'bg-gray-100 text-gray-800', label: 'General' }
        };
        const badge = badges[category] || badges.general;
        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                {badge.label}
            </span>
        );
    };

    const getTypeBadge = (type) => {
        const badges = {
            email: { color: 'bg-indigo-100 text-indigo-800', label: 'Email' },
            sms: { color: 'bg-pink-100 text-pink-800', label: 'SMS' },
            mixed: { color: 'bg-orange-100 text-orange-800', label: 'Mixed' }
        };
        const badge = badges[type] || badges.email;
        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                {badge.label}
            </span>
        );
    };

    const filteredTemplates = templates.filter(template => {
        const matchesSearch = !searchTerm || 
            template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
        const matchesType = typeFilter === 'all' || template.workflow_type === typeFilter;
        return matchesSearch && matchesCategory && matchesType;
    });

    return (
        <div>
            <div className="mb-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <FileText size={24} />
                            Workflow Templates
                        </h1>
                        <p className="text-gray-600">
                            Create and manage reusable workflow templates for quick workflow creation.
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/admin/workflow-builder')}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <Plus size={18} />
                        New Workflow
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border mb-6 p-4">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search templates..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Categories</option>
                            <option value="sales">Sales</option>
                            <option value="service">Service</option>
                            <option value="welcome">Welcome</option>
                            <option value="reminder">Reminder</option>
                            <option value="general">General</option>
                        </select>
                    </div>
                    <div>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Types</option>
                            <option value="email">Email</option>
                            <option value="sms">SMS</option>
                            <option value="mixed">Mixed</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Templates List */}
            {loading ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <Loader className="animate-spin mx-auto" size={32} />
                    <p className="text-gray-600 mt-4">Loading templates...</p>
                </div>
            ) : filteredTemplates.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <FileText className="mx-auto text-gray-400" size={48} />
                    <p className="text-gray-600 mt-4">
                        {searchTerm || categoryFilter !== 'all' || typeFilter !== 'all' 
                            ? 'No templates match your filters.' 
                            : 'No workflow templates found. Create a workflow and save it as a template to get started.'}
                    </p>
                    {!searchTerm && categoryFilter === 'all' && typeFilter === 'all' && (
                        <button
                            onClick={() => navigate('/admin/workflow-builder')}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            Create Your First Workflow
                        </button>
                    )}
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Template Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Category
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredTemplates.map((template) => (
                                    <tr key={template.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {template.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-600 max-w-md truncate">
                                                {template.description || 'No description'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getCategoryBadge(template.category)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getTypeBadge(template.workflow_type)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {template.created_at 
                                                ? new Date(template.created_at).toLocaleDateString()
                                                : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => applyTemplate(template.id)}
                                                    className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                                                    title="Create workflow from template"
                                                >
                                                    <Zap size={16} />
                                                    Use
                                                </button>
                                                <button
                                                    onClick={() => deleteTemplate(template.id)}
                                                    className="text-red-600 hover:text-red-900 flex items-center gap-1"
                                                    title="Delete template"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminWorkflowTemplatesPage;

